import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import MapPicker, { PlaceSelection } from './MapPicker';
import { Building2 } from 'lucide-react';

interface CreateVenueModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: string | null;
  onCreated?: () => void;
  editVenue?: any;
  onSaved?: () => void;
  onToast?: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
}

// Removed legacy Places Autocomplete; rely on MapPicker + Geocoding for address selection

export default function CreateVenueModal({ isOpen, onClose, token, onCreated, editVenue, onSaved, onToast }: CreateVenueModalProps) {
  const NAME_MAX = 80;
  const LOC_MAX = 80;
  const ADDR_MAX = 160;
  const CITY_MAX = 60;
  const STATE_MAX = 60;
  const COUNTRY_MAX = 60;
  const DESC_MAX = 500;
  const ABOUT_MAX = 200;
  const CATEGORY_OPTIONS = [
    { value: 'hotel', label: 'Hotel' },
    { value: 'gardens', label: 'Gardens' },
    { value: 'sports-grounds', label: 'Sports Grounds' },
    { value: 'stadium', label: 'Stadium' },
    { value: 'halls', label: 'Halls' },
    { value: 'bar-and-restaurant', label: 'Bar & Restaurant' },
    { value: 'other', label: 'Other' },
  ];
  const [form, setForm] = useState({
    name: '',
    locationName: '',
    address: '',
    city: '',
    state: '',
    country: '',
    coordinates: undefined as undefined | { lat: number; lng: number },
    capacity: 50,
    category: 'other',
    aboutShort: '',
    images: [] as string[],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (editVenue && isOpen) {
      setForm({
        name: (editVenue.name || '').slice(0, NAME_MAX),
        locationName: (editVenue.location?.name || '').slice(0, LOC_MAX),
        address: (editVenue.location?.address || '').slice(0, ADDR_MAX),
        city: (editVenue.location?.city || '').slice(0, CITY_MAX),
        state: (editVenue.location?.state || '').slice(0, STATE_MAX),
        country: (editVenue.location?.country || '').slice(0, COUNTRY_MAX),
        capacity: editVenue.capacity?.max || 50,
        category: (editVenue.category || 'other'),
        aboutShort: (editVenue.aboutShort || '').slice(0, ABOUT_MAX),
        images: Array.isArray(editVenue.images) ? editVenue.images : [],
      });
    } else if (isOpen && !editVenue) {
      setForm({ name: '', locationName: '', address: '', city: '', state: '', country: '', coordinates: undefined, capacity: 50, category: 'other', aboutShort: '', images: [] });
    }
  }, [editVenue, isOpen]);

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.currentTarget;
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (!files.length || !token) return;
    setError(null);
    setUploading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` } as any;
      const uploaded: string[] = [];
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) {
          setError('Each image must be under 10MB');
          continue;
        }
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API_URL.replace(/\/api$/, '')}/api/files/upload`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        if (res.data?.url) uploaded.push(res.data.url);
      }
      const next = [...form.images, ...uploaded].slice(0, 10);
      setForm({ ...form, images: next });
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to upload image(s)');
    } finally {
      setUploading(false);
      // Reset the input value so re-selecting same files triggers change
      if (input) input.value = '';
    }
  }

  function removeImage(idx: number) {
    const next = form.images.filter((_, i) => i !== idx);
    setForm({ ...form, images: next });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (!token) throw new Error('Not authenticated');
      const payload = {
        name: (form.name || '').slice(0, NAME_MAX),
        location: {
          name: (form.locationName || form.name || '').slice(0, LOC_MAX),
          address: (form.address || '').slice(0, ADDR_MAX),
          city: (form.city || '').slice(0, CITY_MAX),
          state: (form.state || '').slice(0, STATE_MAX),
          country: (form.country || '').slice(0, COUNTRY_MAX),
          coordinates: form.coordinates,
        },
        capacity: { max: Number(form.capacity) },
        category: form.category,
        aboutShort: (form.aboutShort || '').slice(0, ABOUT_MAX),
        images: form.images,
      };
      // Backward compatibility: also set description from aboutShort
      (payload as any).description = (form.aboutShort || '').slice(0, ABOUT_MAX);
      if (editVenue?._id) {
        const res = await axios.put(`${API_URL}/venues/${editVenue._id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        const count = res.data?.updatedEventCount || 0;
        onSaved && onSaved();
        if (count > 0 && onToast) onToast(`Updated ${count} linked ${count === 1 ? 'event' : 'events'} with new location.`, 'success');
      } else {
        await axios.post(`${API_URL}/venues/create`, payload, { headers: { Authorization: `Bearer ${token}` } });
        onCreated && onCreated();
      }
      onClose();
      setForm({ name: '', locationName: '', address: '', city: '', state: '', country: '', coordinates: undefined, capacity: 50, category: 'other', aboutShort: '', images: [] });
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Failed to create venue');
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto overscroll-contain">
      <div className="min-h-full flex items-center justify-center p-4 bg-black/50">
        <div className="w-full max-w-2xl mx-auto p-[1px] rounded-2xl bg-gradient-to-r from-cyan-400 to-purple-500 shadow-2xl">
          <div className="themed-card rounded-2xl flex flex-col max-h-[90vh]" style={{ background: 'var(--card)' }}>
            <div className="px-5 py-4 border-b flex items-center justify-between sticky top-0 z-10" style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
            <div className="flex items-center gap-3">
              <div className="h-2 w-20 rounded-full bg-gradient-to-r from-[var(--accent-start)] to-[var(--accent-end)]" />
              <Building2 className="w-5 h-5 text-cyan-500/80" />
              <div className="leading-tight">
                <h3 className="text-lg font-semibold">{editVenue ? 'Edit Venue' : 'Create Venue'}</h3>
                <p className="text-xs text-theme-secondary">Add details for your venue</p>
              </div>
            </div>
            <button className="px-3 py-1 rounded-md border hover:bg-white/40 dark:hover:bg-slate-800/40" style={{ borderColor: 'var(--border)' }} onClick={onClose}>Close</button>
            </div>
            <div className="px-5 py-4 flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' as any }}>
            {error && <p className="text-red-600 mb-3 text-sm">{error}</p>}
            <div className="mb-4 rounded-xl border p-3 text-sm text-theme-secondary" style={{ borderColor: 'var(--border)' }}>
              Tip: After you approve a booking request, generate a one‑time booking token from My Venues → Requests. Share it in the booking chat. Creators will use that token when creating events to link them to your venue. You can extend or revoke tokens anytime.
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-2">Venue Name *</label>
                  <input className="input w-full" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value.slice(0, NAME_MAX) })} maxLength={NAME_MAX} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-2">Location Name (optional)</label>
                  <input className="input w-full" value={form.locationName} onChange={(e) => setForm({ ...form, locationName: e.target.value.slice(0, LOC_MAX) })} maxLength={LOC_MAX} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-2">Category *</label>
                  <select
                    className="input w-full"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    required
                  >
                    {CATEGORY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-2">Brief About (max {ABOUT_MAX} chars)</label>
                  <input
                    className="input w-full"
                    value={form.aboutShort}
                    onChange={(e) => setForm({ ...form, aboutShort: e.target.value.slice(0, ABOUT_MAX) })}
                    maxLength={ABOUT_MAX}
                    placeholder="Short summary shown on cards"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-2">Address</label>
                <input className="input w-full" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value.slice(0, ADDR_MAX) })} maxLength={ADDR_MAX} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-2">City</label>
                  <input className="input w-full" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value.slice(0, CITY_MAX) })} maxLength={CITY_MAX} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-2">State/Province</label>
                  <input className="input w-full" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.slice(0, STATE_MAX) })} maxLength={STATE_MAX} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-2">Country</label>
                  <input className="input w-full" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.slice(0, COUNTRY_MAX) })} maxLength={COUNTRY_MAX} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-2">Pick on Map (optional)</label>
                <MapPicker
                  value={{
                    name: form.locationName || form.name || undefined,
                    address: form.address || undefined,
                    city: form.city || undefined,
                    state: form.state || undefined,
                    country: form.country || undefined,
                    coordinates: form.coordinates,
                  }}
                  height={320}
                  onChange={(next: PlaceSelection) => {
                    setForm({
                      ...form,
                      locationName: (next.name || form.locationName || '').slice(0, LOC_MAX),
                      address: (next.address || form.address || '').slice(0, ADDR_MAX),
                      city: (next.city || form.city || '').slice(0, CITY_MAX),
                      state: (next.state || form.state || '').slice(0, STATE_MAX),
                      country: (next.country || form.country || '').slice(0, COUNTRY_MAX),
                      coordinates: next.coordinates,
                    });
                  }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input className="input md:col-span-1" type="number" min="1" placeholder="Capacity (max)" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })} required />
                <div className="md:col-span-2" />
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-2">Venue Photos</label>
                <input type="file" accept="image/*" multiple onChange={handleImageUpload} id="venue-images" className="hidden" />
                <label htmlFor="venue-images" className="flex items-center justify-center gap-2 w-full px-4 py-8 rounded-xl cursor-pointer transition-colors border-2 border-dashed hover:border-cyan-400/70" style={{ borderColor: 'var(--border)' }}>
                  {uploading ? <span className="text-theme-secondary">Uploading…</span> : <span className="text-theme-secondary">Click to upload up to 10 photos</span>}
                </label>
                <p className="mt-1 text-xs text-theme-secondary">Tip: JPG/PNG/WebP, up to 10MB each. Max 10 photos.</p>
                {form.images.length > 0 && (
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {form.images.map((url, idx) => (
                      <div key={url + idx} className="relative group">
                        <img src={url} alt={`Venue ${idx + 1}`} className="w-full h-24 object-cover rounded-lg" />
                        <button type="button" onClick={() => removeImage(idx)} className="absolute top-1 right-1 px-2 py-1 text-xs rounded bg-black/60 text-white opacity-0 group-hover:opacity-100">Remove</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <button type="button" className="px-4 py-2 rounded-md border hover:bg-white/40 dark:hover:bg-slate-800/40" style={{ borderColor: 'var(--border)' }} onClick={onClose}>Cancel</button>
                <button type="submit" className="px-4 py-2 rounded-md bg-teal-600 text-white hover:bg-teal-500" disabled={loading || uploading}>{(loading || uploading) ? (editVenue ? 'Saving...' : 'Creating...') : (editVenue ? 'Save Changes' : 'Create Venue')}</button>
              </div>
            </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
